import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { PRINT_FONTS, PrintLayers, ensurePrintFontsLoaded } from "@/lib/customPrint";
import { toast } from "sonner";
import { Loader2, Upload, Check, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

const emptyText = () => ({ value: "", x: 50, y: 50, size: 18, color: "#111111", rotation: 0, font: "sans" });

export default function ProductCustomizer({ product, slug, onChange, onConfirm, initial, hideIntro }) {
  const cfg = product.custom_print;
  const [image, setImage] = useState(initial?.image || null);
  const [text, setText] = useState({ ...emptyText(), ...(initial?.text || {}) });
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(null);
  const layerRef = useRef(null);
  const dragRef = useRef(null);
  const imageRef = useRef(image);
  const textRef = useRef(text);
  imageRef.current = image;
  textRef.current = text;
  dragRef.current = drag;

  const allowImage = cfg.allow_image !== false && cfg.allow !== "text";
  const allowText = cfg.allow_text !== false && cfg.allow !== "image";

  useEffect(() => {
    ensurePrintFontsLoaded();
  }, []);

  const emit = (nextImage, nextText) => {
    const img = nextImage !== undefined ? nextImage : imageRef.current;
    const tx = nextText !== undefined ? nextText : textRef.current;
    onChange?.({
      image: allowImage && img?.url ? img : null,
      text: allowText && String(tx.value || "").trim() ? tx : null,
      preview_url: "",
    });
  };

  const pct = (e) => {
    const el = layerRef.current;
    if (!el) return { x: 50, y: 50 };
    const r = el.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return {
      x: Math.max(0, Math.min(100, ((pt.clientX - r.left) / r.width) * 100)),
      y: Math.max(0, Math.min(100, ((pt.clientY - r.top) / r.height) * 100)),
    };
  };

  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      e.preventDefault();
      const d = dragRef.current;
      if (!d) return;
      const p = pct(e);
      const x = Math.max(0, Math.min(100, p.x - d.ox));
      const y = Math.max(0, Math.min(100, p.y - d.oy));
      if (d.kind === "image") {
        setImage((img) => {
          if (!img) return img;
          const next = { ...img, x, y };
          emit(next, textRef.current);
          return next;
        });
        return;
      }
      setText((tx) => {
        const next = { ...tx, x, y };
        emit(imageRef.current, next);
        return next;
      });
    };
    const up = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [drag]);

  const startLayerDrag = (kind, e) => {
    e.preventDefault();
    e.stopPropagation();
    const p = pct(e);
    const target = kind === "image" ? imageRef.current : textRef.current;
    if (kind === "image" && !target?.url) return;
    setDrag({ kind, ox: p.x - (target?.x ?? 50), oy: p.y - (target?.y ?? 50) });
  };

  const uploadArt = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/store/${slug}/custom-print/upload`, fd);
      const next = { url: data.url, x: 50, y: 50, scale: 1, rotation: 0 };
      setImage(next);
      emit(next, textRef.current);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setUploading(false);
    }
  };

  const setTextField = (patch) => {
    setText((tx) => {
      const next = { ...tx, ...patch };
      emit(imageRef.current, next);
      return next;
    });
  };

  const bumpSize = (delta) => {
    setTextField({ size: Math.max(8, Math.min(48, Number(text.size || 18) + delta)) });
  };

  return (
    <div className="space-y-4" data-testid="product-custom-print">
      {hideIntro ? null : (
      <div>
        <h2 className="text-lg font-bold text-stone-900">Customize Product</h2>
        <p className="text-sm text-stone-500 mt-0.5">Your design stays inside the print area the store defined.</p>
      </div>
      )}
      <div className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden border border-stone-200 bg-stone-50">
        <img src={mediaUrl(cfg.base_image_url)} alt="" className="block w-full h-auto" />
        <div className="absolute inset-0">
          <PrintLayers
            printArea={cfg.print_area}
            image={image}
            text={allowText && text.value ? text : null}
            interactive
            layerRef={layerRef}
            onLayerPointerDown={startLayerDrag}
          />
        </div>
      </div>

      {allowImage ? (
        <div className="space-y-2">
          <Label>Upload your design</Label>
          <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-stone-300 text-sm font-semibold cursor-pointer hover:bg-stone-50" data-testid="cp-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {image?.url ? "Replace image" : "Upload image"}
            <input type="file" hidden accept="image/*" onChange={(e) => uploadArt(e.target.files?.[0])} />
          </label>
          {image?.url ? (
            <div className="flex items-center gap-3 text-sm text-stone-600">
              <span>Zoom</span>
              <input type="range" min="0.3" max="4" step="0.05" value={image.scale || 1} onChange={(e) => { const next = { ...image, scale: Number(e.target.value) }; setImage(next); emit(next, text); }} data-testid="cp-zoom" />
              <span>Rotate</span>
              <input type="range" min="-180" max="180" step="1" value={image.rotation || 0} onChange={(e) => { const next = { ...image, rotation: Number(e.target.value) }; setImage(next); emit(next, text); }} data-testid="cp-rotate" />
            </div>
          ) : null}
        </div>
      ) : null}

      {allowText ? (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Custom text</Label>
            <Input className="mt-1" maxLength={80} value={text.value} onChange={(e) => setTextField({ value: e.target.value })} placeholder="Name or message" data-testid="cp-text" />
            <p className="text-[11px] text-stone-500 mt-1">Drag the text on the preview to place it. Use size and font below.</p>
          </div>
          <div className="sm:col-span-2">
            <Label>Font style</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="cp-text-font">
              {PRINT_FONTS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`cp-text-font-${f.id}`}
                  onClick={() => setTextField({ font: f.id })}
                  className={`h-8 px-2.5 rounded-full text-xs font-semibold border ${(text.font || "sans") === f.id ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200"}`}
                  style={{ fontFamily: f.family }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Text color</Label>
            <input type="color" className="mt-1 h-10 w-full rounded border border-stone-200" value={text.color} onChange={(e) => setTextField({ color: e.target.value })} data-testid="cp-text-color" />
          </div>
          <div>
            <Label>Text size</Label>
            <div className="mt-2 flex items-center gap-2">
              <button type="button" className="h-8 w-8 rounded-full border border-stone-200 grid place-items-center" data-testid="cp-text-size-down" onClick={() => bumpSize(-2)} aria-label="Smaller text">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <input type="range" className="flex-1" min="8" max="48" value={Number(text.size || 18)} onChange={(e) => setTextField({ size: Number(e.target.value) })} data-testid="cp-text-size" />
              <button type="button" className="h-8 w-8 rounded-full border border-stone-200 grid place-items-center" data-testid="cp-text-size-up" onClick={() => bumpSize(2)} aria-label="Larger text">
                <Plus className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs tabular-nums text-stone-500 w-8" data-testid="cp-text-size-value">{Math.round(Number(text.size || 18))}</span>
            </div>
          </div>
        </div>
      ) : null}

      <p className="text-xs text-stone-500">Drag the design or text inside the print area. Scroll or use zoom to resize an uploaded image. Anything outside the area is hidden.</p>
      {onConfirm ? (
        <Button
          type="button"
          className="w-full sm:w-auto rounded-full btn-store-primary"
          data-testid="cp-confirm"
          disabled={!image?.url && !String(text.value || "").trim()}
          onClick={() => onConfirm({
            image: allowImage && image?.url ? image : null,
            text: allowText && String(text.value || "").trim() ? text : null,
            preview_url: "",
          })}
        >
          <Check className="w-4 h-4 mr-1.5" /> Save customization
        </Button>
      ) : null}
    </div>
  );
}
